import React, { useEffect } from "react"
import { X, SlidersHorizontal } from "lucide-react"
import {
    INFLUENCER_CREATOR_SIZE_OPTIONS, INFLUENCER_WITHIN_DAYS_OPTIONS,
} from "../constants"

const toggleValue = (arr, value) => (arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value])

const ChipToggle = ({ active, onClick, color = "#F6255A", children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`influencer-filter-chip${active ? " is-active" : ""}`}
        style={active ? { borderColor: color, color, background: `${color}18` } : undefined}
    >
        {children}
    </button>
)

const FilterGroup = ({ label, children }) => (
    <div className="influencer-filter-group">
        <span className="influencer-filter-group-label">{label}</span>
        <div className="influencer-filter-chip-row">{children}</div>
    </div>
)

/**
 * The narrowing side of TOP_INFLUENCER_FILTERS.md §2, same popup-modal
 * pattern as FiltersPanel.jsx (video tabs). One structural difference from
 * that panel: this endpoint's `filter_options` facets (§6) are plain
 * `{value: count}` maps, not arrays of `{value, count}` - so counts are read
 * with `facets?.group?.[value]` here instead of `.find(...)`. Facets are
 * fetched fresh on every filter change (useTopInfluencers passes
 * `include_facets=true` on every call), since §6 says they narrow along with
 * whatever the user has already selected - unlike the video tabs' facets,
 * which describe the whole app and are fetched once.
 */
const InfluencerFiltersPanel = ({ filters, onChange, facets, onClear, activeCount, onClose }) => {
    const set = (key, value) => onChange({ ...filters, [key]: value })

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === "Escape") onClose() }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [onClose])

    const creatorSizeFacet = facets?.creator_size || {}
    const verifiedFacet = facets?.verified || {}
    const countryFacet = facets?.country || {}
    const countryOptions = Object.entries(countryFacet).filter(([, count]) => count > 0)

    return (
        <div className="influencer-filters-overlay" onClick={onClose}>
            <div className="influencer-filters-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Filters">
                <div className="influencer-filters-modal-header">
                    <span className="d-flex align-items-center gap-2">
                        <SlidersHorizontal size={16} color="#F6255A" />
                        <h3>Filters</h3>
                    </span>
                    <button type="button" onClick={onClose} className="influencer-filters-close" aria-label="Close filters">
                        <X size={18} />
                    </button>
                </div>

                <div className="influencer-filters-modal-body">
                    {/* Platform is filtered from the toolbar directly
                        (InfluencerPlatformToggle, next to this Filters
                        button) rather than inside the modal - it's styled to
                        match the video tabs' own PlatformTabs switcher, so it
                        doesn't need to be here too. */}
                    <FilterGroup label="Audience size">
                        {INFLUENCER_CREATOR_SIZE_OPTIONS.map(opt => {
                            const count = creatorSizeFacet[opt.value]
                            return (
                                <ChipToggle key={opt.value} active={filters.creatorSize.includes(opt.value)} onClick={() => set("creatorSize", toggleValue(filters.creatorSize, opt.value))}>
                                    {opt.label}{count !== undefined ? ` (${count})` : ""}
                                </ChipToggle>
                            )
                        })}
                    </FilterGroup>

                    <div className="influencer-filter-row-inline">
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Min. followers</span>
                            <input type="number" min="0" placeholder="e.g. 250000" className="influencer-filter-input" value={filters.minFollowers} onChange={e => set("minFollowers", e.target.value)} />
                        </div>
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Max. followers</span>
                            <input type="number" min="0" placeholder="e.g. 2000000" className="influencer-filter-input" value={filters.maxFollowers} onChange={e => set("maxFollowers", e.target.value)} />
                        </div>
                    </div>

                    <div className="influencer-filter-row-inline">
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Min. posts</span>
                            <input type="number" min="0" placeholder="e.g. 10" className="influencer-filter-input" value={filters.minPosts} onChange={e => set("minPosts", e.target.value)} />
                        </div>
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Max. posts</span>
                            <input type="number" min="0" placeholder="e.g. 500" className="influencer-filter-input" value={filters.maxPosts} onChange={e => set("maxPosts", e.target.value)} />
                        </div>
                    </div>

                    {/* Lifetime channel views - only YouTube reports this
                        (§2), so it narrows to YouTube in practice. */}
                    <div className="influencer-filter-row-inline">
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Min. total views</span>
                            <input type="number" min="0" placeholder="e.g. 100000 (YouTube only)" className="influencer-filter-input" value={filters.minTotalViews} onChange={e => set("minTotalViews", e.target.value)} />
                        </div>
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Max. total views</span>
                            <input type="number" min="0" placeholder="e.g. 10000000 (YouTube only)" className="influencer-filter-input" value={filters.maxTotalViews} onChange={e => set("maxTotalViews", e.target.value)} />
                        </div>
                    </div>

                    <div className="influencer-filter-row-inline">
                        {/* The "who covers this space most" filter (§2) -
                            pairs naturally with sort=coverage. */}
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Min. posts about apps</span>
                            <input type="number" min="0" placeholder="e.g. 2" className="influencer-filter-input" value={filters.minPostsAboutApps} onChange={e => set("minPostsAboutApps", e.target.value)} />
                        </div>
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">App ID</span>
                            <input type="text" placeholder="e.g. 24775738222115749" className="influencer-filter-input" value={filters.appId} onChange={e => set("appId", e.target.value)} />
                        </div>
                    </div>

                    <div className="influencer-filter-row-inline">
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Username</span>
                            <input type="text" placeholder="e.g. poweronvr" className="influencer-filter-input" value={filters.username} onChange={e => set("username", e.target.value)} />
                        </div>
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Country</span>
                            {countryOptions.length > 0 ? (
                                <select className="influencer-filter-select" value={filters.country} onChange={e => set("country", e.target.value)}>
                                    <option value="">All countries</option>
                                    {countryOptions.map(([name, count]) => (
                                        <option key={name} value={name}>{name} ({count})</option>
                                    ))}
                                </select>
                            ) : (
                                <input type="text" placeholder="e.g. United Kingdom" className="influencer-filter-input" value={filters.country} onChange={e => set("country", e.target.value)} />
                            )}
                        </div>
                    </div>

                    <div className="influencer-filter-group">
                        <span className="influencer-filter-group-label">Search</span>
                        <input
                            type="text"
                            placeholder="Search username or display name…"
                            className="influencer-filter-input"
                            style={{ width: "100%" }}
                            value={filters.search}
                            onChange={e => set("search", e.target.value)}
                        />
                    </div>

                    <FilterGroup label="Account quality & outreach">
                        <ChipToggle active={filters.verifiedOnly} onClick={() => set("verifiedOnly", !filters.verifiedOnly)}>
                            Verified only{verifiedFacet.verified !== undefined ? ` (${verifiedFacet.verified})` : ""}
                        </ChipToggle>
                        <ChipToggle active={filters.hasEmail} onClick={() => set("hasEmail", !filters.hasEmail)}>
                            Has email{facets?.with_email !== undefined ? ` (${facets.with_email})` : ""}
                        </ChipToggle>
                        <ChipToggle active={filters.hasLinks} onClick={() => set("hasLinks", !filters.hasLinks)}>
                            Has links{facets?.with_links !== undefined ? ` (${facets.with_links})` : ""}
                        </ChipToggle>
                        <ChipToggle active={filters.hasDescription} onClick={() => set("hasDescription", !filters.hasDescription)}>
                            Has bio
                        </ChipToggle>
                        <ChipToggle active={filters.excludePrivate} onClick={() => set("excludePrivate", !filters.excludePrivate)}>
                            Exclude private
                        </ChipToggle>
                        <ChipToggle active={filters.mirroredImageOnly} onClick={() => set("mirroredImageOnly", !filters.mirroredImageOnly)}>
                            Has profile photo{facets?.mirrored_images !== undefined ? ` (${facets.mirrored_images})` : ""}
                        </ChipToggle>
                    </FilterGroup>

                    <div className="influencer-filter-row-inline">
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Discovered within</span>
                            <select className="influencer-filter-select" value={filters.discoveredWithinDays} onChange={e => set("discoveredWithinDays", e.target.value)}>
                                {INFLUENCER_WITHIN_DAYS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="influencer-filter-group">
                            <span className="influencer-filter-group-label">Refreshed within</span>
                            <select className="influencer-filter-select" value={filters.refreshedWithinDays} onChange={e => set("refreshedWithinDays", e.target.value)}>
                                {INFLUENCER_WITHIN_DAYS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <FilterGroup label="Freshness">
                        <ChipToggle active={filters.staleOnly} onClick={() => set("staleOnly", !filters.staleOnly)}>
                            Due for refresh
                        </ChipToggle>
                    </FilterGroup>
                </div>

                <div className="influencer-filters-modal-footer">
                    {activeCount > 0 ? (
                        <button type="button" onClick={onClear} className="influencer-filter-clear">
                            <X size={13} /> Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
                        </button>
                    ) : <span />}
                    <button type="button" onClick={onClose} className="influencer-btn-primary" style={{ width: "auto" }}>
                        Done
                    </button>
                </div>
            </div>

            <style>{`
                .influencer-filters-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    z-index: 10050;
                }
                .influencer-filters-modal {
                    width: min(640px, 100%);
                    max-height: 85vh;
                    background: #21222d;
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 16px;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.5);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                .influencer-filters-modal-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 18px 20px;
                    border-bottom: 1px solid rgba(255,255,255,0.06);
                    flex-shrink: 0;
                }
                .influencer-filters-modal-header h3 {
                    color: #fff;
                    font-weight: 600;
                    font-size: 17px;
                    margin: 0;
                    font-family: Rubik, sans-serif;
                }
                .influencer-filters-close {
                    background: rgba(255,255,255,0.05);
                    border: none;
                    color: #e8e8e8;
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }
                .influencer-filters-close:hover { background: rgba(255,255,255,0.1); }
                .influencer-filters-modal-body {
                    display: flex;
                    flex-direction: column;
                    gap: 18px;
                    padding: 20px;
                    overflow-y: auto;
                }
                .influencer-filters-modal-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 16px 20px;
                    border-top: 1px solid rgba(255,255,255,0.06);
                    flex-shrink: 0;
                }
                .influencer-filter-group { display: flex; flex-direction: column; gap: 8px; }
                .influencer-filter-group-label {
                    font-size: 11px;
                    font-weight: 700;
                    color: #9ca3af;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                }
                .influencer-filter-chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
                .influencer-filter-row-inline { display: flex; flex-wrap: wrap; gap: 16px; }
                .influencer-filter-row-inline .influencer-filter-group { flex: 1 1 180px; }
                .influencer-filter-chip {
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.1);
                    color: #e8e8e8;
                    padding: 6px 12px;
                    border-radius: 999px;
                    font-size: 12.5px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background 0.15s ease, border-color 0.15s ease;
                }
                .influencer-filter-chip:hover { background: rgba(255,255,255,0.1); }
                .influencer-filter-chip.is-active { border-width: 1px; }
                .influencer-filter-input, .influencer-filter-select {
                    background: #1c1d27;
                    border: 1px solid rgba(255,255,255,0.1);
                    color: #fff;
                    padding: 8px 10px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-family: Rubik, sans-serif;
                    width: 100%;
                    min-width: 0;
                }
                .influencer-filter-input:focus, .influencer-filter-select:focus { outline: none; border-color: #F6255A; }
                .influencer-filter-input::placeholder { color: #6b7280; opacity: 1; }
                .influencer-filter-clear {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(246,37,90,0.1);
                    border: 1px solid rgba(246,37,90,0.3);
                    color: #F6255A;
                    padding: 8px 12px;
                    border-radius: 8px;
                    font-size: 12.5px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    white-space: nowrap;
                }
                .influencer-filter-clear:hover { background: rgba(246,37,90,0.18); }

                @media (max-width: 575.98px) {
                    .influencer-filters-overlay { padding: 0; align-items: flex-end; }
                    .influencer-filters-modal {
                        width: 100%;
                        max-height: 90vh;
                        border-radius: 16px 16px 0 0;
                    }
                    .influencer-filters-modal-footer { flex-direction: column-reverse; align-items: stretch; }
                    .influencer-filters-modal-footer .influencer-filter-clear,
                    .influencer-filters-modal-footer .influencer-btn-primary { width: 100%; justify-content: center; }
                }
            `}</style>
        </div>
    )
}

export default InfluencerFiltersPanel
